// The walkthrough player (18-help-and-training.md §18.1). The page dims, the element a step is about
// stays lit with a ring around it, and a card beside it holds the title, the text, the step count and
// the buttons. A step with no element, or whose element is not on the page right now, is a card in
// the middle of the screen; on a phone the card sits at the bottom. Steps are plain text, never HTML.
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import {
  demoStepMs,
  type WalkthroughMode,
  type WalkthroughOutcome,
  type WalkthroughStep,
} from '@ecobox/domain/walkthroughs';
import { Button } from './Button';
import { tourSelector } from './tourTargets';

export interface TourEnd {
  /** `completed` when every step was seen (Finish, the demo's end, or Close on the last step). */
  outcome: WalkthroughOutcome;
  mode: WalkthroughMode;
  /** How many different steps were shown. */
  stepsSeen: number;
}

export interface TourLabels {
  back: string;
  next: string;
  finish: string;
  close: string;
  pause: string;
  play: string;
  demo: string;
  /** "3 of 7" */
  count: (step: number, total: number) => string;
  /** The dialog's name for screen readers. */
  dialog: (title: string, mode: WalkthroughMode) => string;
  /** Under a step whose element is not on the page at the moment. */
  missing: string;
}

export const TOUR_LABELS_EN: TourLabels = {
  back: 'Back',
  next: 'Next',
  finish: 'Finish',
  close: 'Close',
  pause: 'Pause',
  play: 'Play',
  demo: 'Demo',
  count: (step, total) => `${step} of ${total}`,
  dialog: (title, mode) => `${title}: ${mode === 'demo' ? 'demo' : 'walkthrough'}`,
  missing:
    'This part is not on the screen right now. You will find it here when there is something to show.',
};

export interface TourProps {
  /** The page's title, for the dialog's name. */
  title: string;
  /** The steps this person is shown (already filtered with `visibleSteps`). */
  steps: readonly WalkthroughStep[];
  mode: WalkthroughMode;
  /** Called exactly once, when the walkthrough ends. */
  onEnd: (end: TourEnd) => void;
  labels?: Partial<TourLabels> | undefined;
  /** Where focus goes at the end (the Help button). Defaults to what had focus at the start. */
  returnFocusTo?: RefObject<HTMLElement | null> | undefined;
  /** How long the demo stays on a step. Defaults to `demoStepMs`. */
  stepMs?: ((step: WalkthroughStep) => number) | undefined;
}

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type CardPlacement =
  | { kind: 'center' }
  | { kind: 'sheet' }
  | { kind: 'beside'; side: 'below' | 'above' | 'right' | 'left'; top: number; left: number };

/** At this width and below the card is a bottom sheet. */
export const PHONE_MAX_WIDTH = 640;
const MARGIN = 16;
const GAP = 12;
const RING_PAD = 6;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Where the card goes for a target (in viewport coordinates), a card size and a viewport. */
export function placeCard(
  target: Box | null,
  card: { width: number; height: number },
  view: { width: number; height: number },
): CardPlacement {
  if (view.width <= PHONE_MAX_WIDTH) return { kind: 'sheet' };
  if (!target) return { kind: 'center' };
  const left = clamp(target.left, MARGIN, Math.max(MARGIN, view.width - card.width - MARGIN));
  const top = clamp(target.top, MARGIN, Math.max(MARGIN, view.height - card.height - MARGIN));
  const below = target.top + target.height + RING_PAD + GAP;
  if (below + card.height + MARGIN <= view.height)
    return { kind: 'beside', side: 'below', top: below, left };
  const above = target.top - RING_PAD - GAP - card.height;
  if (above >= MARGIN) return { kind: 'beside', side: 'above', top: above, left };
  const right = target.left + target.width + RING_PAD + GAP;
  if (right + card.width + MARGIN <= view.width)
    return { kind: 'beside', side: 'right', top, left: right };
  const leftSide = target.left - RING_PAD - GAP - card.width;
  if (leftSide >= MARGIN) return { kind: 'beside', side: 'left', top, left: leftSide };
  // The element fills the screen: the card sits over its lower part.
  return {
    kind: 'beside',
    side: 'below',
    top: Math.max(MARGIN, view.height - card.height - MARGIN),
    left,
  };
}

/**
 * The part of the element that is on the screen: the ring never runs off it. An element taller than
 * the screen is ringed where it shows, with the margin kept for the ring itself.
 */
export function visibleBox(
  r: { top: number; left: number; width: number; height: number },
  view: { width: number; height: number },
): Box {
  const pad = RING_PAD + 2;
  const top = Math.max(r.top, pad);
  const left = Math.max(r.left, pad);
  const bottom = Math.min(r.top + r.height, view.height - pad);
  const right = Math.min(r.left + r.width, view.width - pad);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

const FOCUSABLE = 'button:not([disabled]),[href],[tabindex]:not([tabindex="-1"])';

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

const CONTAINERS = new Set(['DIV', 'SECTION', 'UL', 'OL', 'TBODY', 'NAV', 'ASIDE', 'ARTICLE']);

/** A block with nothing in it (a list with no rows yet): ringing it would frame empty space. */
export function isEmptyBlock(el: Element): boolean {
  return CONTAINERS.has(el.tagName) && el.childElementCount === 0 && !el.textContent?.trim();
}

/**
 * The element a step points at, when it is on the page and showing. An empty block, or one with no
 * size at all in a real browser, counts as not there: the card then sits in the middle of the screen.
 */
export function findTarget(target: string | undefined): Element | null {
  if (!target) return null;
  const el = document.querySelector(tourSelector(target));
  if (!el) return null;
  const visible = (el as Element & { checkVisibility?: () => boolean }).checkVisibility;
  if (typeof visible === 'function' && !visible.call(el)) return null;
  if (isEmptyBlock(el)) return null;
  return el;
}

const sameBox = (a: Box | null, b: Box | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height);

export function Tour({ title, steps, mode, onEnd, labels, returnFocusTo, stepMs }: TourProps) {
  const t = { ...TOUR_LABELS_EN, ...labels };
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(mode === 'demo');
  const [box, setBox] = useState<Box | null>(null);
  const [missing, setMissing] = useState(false);
  const [view, setView] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const [cardSize, setCardSize] = useState({ width: 360, height: 220 });
  const card = useRef<HTMLDivElement>(null);
  const primary = useRef<HTMLButtonElement>(null);
  // What had focus when the walkthrough started (read during the first render, before focus moves).
  const [openerEl] = useState(() => document.activeElement as HTMLElement | null);
  const opener = useRef<HTMLElement | null>(openerEl);
  const ended = useRef(false);
  const seen = useRef(1);
  const titleId = useId();
  const bodyId = useId();
  const total = steps.length;
  const step = steps[Math.min(index, Math.max(0, total - 1))];
  const last = index >= total - 1;

  // The latest props, so the listeners below are set up once and never see stale values.
  const latest = useRef({ onEnd, mode, returnFocusTo, stepMs });
  useEffect(() => {
    latest.current = { onEnd, mode, returnFocusTo, stepMs };
  });

  const end = useCallback((outcome: WalkthroughOutcome) => {
    if (ended.current) return;
    ended.current = true;
    const { onEnd: done, mode: m, returnFocusTo: back } = latest.current;
    const target = back?.current ?? opener.current;
    if (target?.isConnected) target.focus();
    done({ outcome, mode: m, stepsSeen: seen.current });
  }, []);

  useEffect(() => {
    seen.current = Math.max(seen.current, index + 1);
  }, [index]);

  const go = useCallback(
    (delta: number) => setIndex((i) => clamp(i + delta, 0, Math.max(0, total - 1))),
    [total],
  );
  const close = useCallback(() => end(last ? 'completed' : 'skipped'), [end, last]);

  // Focus moves into the card, and back to the opener if the tour is taken away without ending.
  useEffect(() => {
    (primary.current ?? card.current)?.focus();
    const first = opener.current;
    return () => {
      const back = latest.current.returnFocusTo?.current ?? first;
      if (!ended.current && back?.isConnected) back.focus();
    };
  }, []);

  useEffect(() => {
    if (total === 0) end('skipped');
  }, [total, end]);

  const measure = useCallback(() => {
    setView((v) =>
      v.width === window.innerWidth && v.height === window.innerHeight
        ? v
        : { width: window.innerWidth, height: window.innerHeight },
    );
    const el = findTarget(step?.target);
    setMissing(step?.target !== undefined && el === null);
    if (!el) {
      setBox(null);
      return;
    }
    const next = visibleBox(el.getBoundingClientRect(), {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    setBox((cur) => (sameBox(cur, next) ? cur : next));
  }, [step]);

  // A new step: bring its element into view, then light it.
  useLayoutEffect(() => {
    const el = findTarget(step?.target);
    if (el && typeof el.scrollIntoView === 'function') {
      // Taller than the screen: its top (the heading) comes into view, not its middle.
      const tall = el.getBoundingClientRect().height > window.innerHeight - 2 * MARGIN;
      el.scrollIntoView({
        block: tall ? 'start' : 'center',
        inline: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
    // Reading where the element is on the screen is the point of this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    measure();
    if (card.current && !card.current.contains(document.activeElement)) card.current.focus();
  }, [step, measure]);

  // Follow the element while the page scrolls or the window changes size.
  useEffect(() => {
    let frame = 0;
    const onMove = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [measure]);

  useLayoutEffect(() => {
    const el = card.current;
    if (!el) return;
    const size = { width: el.offsetWidth || 360, height: el.offsetHeight || 220 };
    setCardSize((cur) => (cur.width === size.width && cur.height === size.height ? cur : size));
  }, [step, missing, view]);

  // Keys: arrows move, Escape closes, Tab stays inside the card.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Tab' && card.current) {
        const nodes = [...card.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
        const first = nodes[0];
        const lastNode = nodes[nodes.length - 1];
        if (!first || !lastNode) return;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === card.current)) {
          e.preventDefault();
          lastNode.focus();
        } else if (!e.shiftKey && (active === lastNode || !card.current.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [close, go]);

  // The demo moves on by itself.
  useEffect(() => {
    if (mode !== 'demo' || !playing || !step) return;
    const ms = (latest.current.stepMs ?? demoStepMs)(step);
    const timer = setTimeout(() => (last ? end('completed') : go(1)), ms);
    return () => clearTimeout(timer);
  }, [mode, playing, step, last, end, go]);

  if (!step) return null;
  const placement = placeCard(box, cardSize, view);
  const style =
    placement.kind === 'beside' ? { top: placement.top, left: placement.left } : undefined;
  const demoMs = mode === 'demo' ? (stepMs ?? demoStepMs)(step) : 0;

  return createPortal(
    <div className="eb-tour" data-mode={mode}>
      <div className="eb-tour__shade" data-dim={box ? 'false' : 'true'} aria-hidden="true" />
      {box ? (
        <div
          className="eb-tour__ring"
          aria-hidden="true"
          style={{
            top: box.top - RING_PAD,
            left: box.left - RING_PAD,
            width: box.width + RING_PAD * 2,
            height: box.height + RING_PAD * 2,
          }}
        />
      ) : null}
      <div
        ref={card}
        className="eb-tour__card"
        role="dialog"
        aria-modal="true"
        aria-label={t.dialog(title, mode)}
        aria-describedby={bodyId}
        data-placement={placement.kind}
        data-side={placement.kind === 'beside' ? placement.side : undefined}
        data-target={box ? step.target : undefined}
        style={style}
        tabIndex={-1}
      >
        <div className="eb-tour__top">
          <span className="eb-tour__count">{t.count(index + 1, total)}</span>
          {mode === 'demo' ? <span className="eb-tour__badge">{t.demo}</span> : null}
        </div>
        <div aria-live="polite">
          <h2 className="eb-tour__title" id={titleId}>
            {step.title}
          </h2>
          <p className="eb-tour__body" id={bodyId}>
            {step.body}
          </p>
          {missing ? <p className="eb-tour__note">{t.missing}</p> : null}
          {mode === 'guided' && step.tryIt ? <p className="eb-tour__try">{step.tryIt}</p> : null}
        </div>
        {mode === 'demo' ? (
          <div className="eb-tour__progress" aria-hidden="true">
            <span
              key={`${index}-${String(playing)}`}
              data-playing={playing ? 'true' : 'false'}
              style={{ animationDuration: `${demoMs}ms` }}
            />
          </div>
        ) : null}
        <div className="eb-tour__actions">
          <Button variant="ghost" size="sm" onClick={close}>
            {t.close}
          </Button>
          <span className="eb-tour__spacer" />
          {mode === 'demo' ? (
            <Button
              ref={primary}
              variant="secondary"
              size="sm"
              aria-pressed={!playing}
              onClick={() => setPlaying((p) => !p)}
            >
              {playing ? t.pause : t.play}
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" disabled={index === 0} onClick={() => go(-1)}>
                {t.back}
              </Button>
              <Button
                ref={primary}
                variant="primary"
                size="sm"
                onClick={() => (last ? end('completed') : go(1))}
              >
                {last ? t.finish : t.next}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
